import React, { useContext, useState } from "react";
import { Link, Navigate, useNavigate } from "react-router-dom";
import { AuthContext } from "../App";
import envConfig from "../envConfig";
import Swal from "sweetalert2";

function Login() {
  const { isAuthenticated, login } = useContext(AuthContext);
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [loading, setLoading] = useState(false);
  const navigate = useNavigate();

  if (isAuthenticated) {
    return <Navigate to="/" replace />;
  }

  const handleLogin = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      const response = await fetch(`${envConfig.API_URL}/auth/login`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify({ email, password }),
      });

      const data = await response.json();

      if (response.ok && data.success) {
        const token = data.token;
        const expiryTime = Date.now() + 12 * 60 * 60 * 1000; // 12 hours in milliseconds

        localStorage.setItem("profishBlue_authToken", token);
        localStorage.setItem("profishBlue_tokenExpiry", expiryTime);

        const userResponse = await fetch(`${envConfig.API_URL}/auth/self`, {
          method: "GET",
          headers: {
            "Content-Type": "application/json",
            Authorization: token,
          },
        });

        const userData = await userResponse.json();

        if (userResponse.ok && userData.success) {
          localStorage.setItem("userDetails", JSON.stringify(userData.data));
        } else {
          console.error("Failed to fetch user details:", userData.message);
        }

        login();
        navigate("/");
      } else {
        Swal.fire({
          title: "Error",
          text: data.message || "Invalid credentials!",
          icon: "error",
          confirmButtonText: "OK",
        });
      }
    } catch (error) {
      Swal.fire({
        title: "Error",
        text: "An error occurred. Please try again.",
        icon: "error",
        confirmButtonText: "OK",
      });
    } finally {
      setLoading(false);
    }
  };

  const handleForgetPassword = async () => {
    // Show SweetAlert2 popup to collect email
    const { value: emailInput } = await Swal.fire({
      title: "Forgot Password",
      input: "email",
      inputLabel: "Enter your email address",
      inputPlaceholder: "Email",
      confirmButtonText: "Send Reset Link",
      cancelButtonText: "Cancel",
      showCancelButton: true,
      inputValidator: (value) => {
        if (!value) {
          return "Email is required!";
        }
        if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(value)) {
          return "Please enter a valid email address!";
        }
      },
    });

    if (emailInput) {
      setLoading(true);
      try {
        const response = await fetch(`${envConfig.API_URL}/auth/forgot_password`, {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
          },
          body: JSON.stringify({ email: emailInput }),
        });

        const data = await response.json();
        if (response.ok && data.success) {
          Swal.fire({
            title: "Success",
            text: "A password reset link has been sent to your email!",
            icon: "success",
            confirmButtonText: "OK",
          });
        } else {
          Swal.fire({
            title: "Error",
            text: data.message || "Failed to send reset link!",
            icon: "error",
            confirmButtonText: "OK",
          });
        }
      } catch (error) {
        Swal.fire({
          title: "Error",
          text: "An error occurred. Please try again.",
          icon: "error",
          confirmButtonText: "OK",
        });
      } finally {
        setLoading(false);
      }
    }
  };

  return (
    <section className="login-section pt-100px">
      <div className="container-fluid">
        <div className="row">
          <div className="col-md-6 pe-0 ps-0 d-none d-md-block">
            <div className="form-left h-100vh">
              <img src="./assets/images/login-img.svg" alt="" className="w-100" />
            </div>
            <div className="lightBox">
              <h6>What can you do?</h6>
              <div className="d-flex justify-content-between align-items-center py-2">
                <div>
                  <p className="mb-0">
                    <img src="./assets/images/message-square.svg" alt="" className="pe-1" /> Send messages to Fish Farmers
                  </p>
                </div>
                <div>
                  <p className="mb-0">
                    <img src="./assets/images/thermometer.svg" alt="" className="pe-1" /> Find out local pond temperature
                  </p>
                </div>
                <div>
                  <p className="mb-0">
                    <img src="./assets/images/time.svg" alt="" className="pe-1" /> See updates time to time
                  </p>
                </div>
              </div>
              <p className="small mb-0">
                Read more about us: <a href="https://worldfishcenter.org/" target="_blank" rel="noopener noreferrer">https://worldfishcenter.org/</a>
              </p>
            </div>
          </div>
          <div className="col-md-6 ps-0">
            <div className="form-right h-100vh bg-white text-white pb-3 pt-3 px-5">
              <div className="logoImg mb-3">
                <div className="d-flex justify-content-start align-items-center">
                  <div> <img
                    src="./assets/images/worldfish-logo.svg"
                    loading="lazy"
                    alt="Logo"
                    height="40"
                  /></div>
                  <div className="vl"></div>
                  <div><h4 className="nav-title mb-0">Blue Resilience / Ulimi Wansomba</h4></div>
                </div>
                {/* <img src="./assets/images/top-login-logo.svg" alt="" /> */}
              </div>
              <h1 className="nav-title-1 mb-0">Tailored decision support platform for aquatic food systems</h1>
              <br />
              <h4>
                <span className="text-dark font-14px" style={{ fontWeight: 'normal' }}>
                  <span className="mb-0" style={{ color: '#1c486b', fontSize: '16px' }}>
                    <strong>Blue Resilience / Ulimi Wansomba</strong>
                  </span>&nbsp;
                  Generates and communicates climate information and advisory services to enable climate-informed decision-making at different scales — from small-scale fish farmers to policy makers — paving the way for climate risk management initiatives for aquatic foods in the SADC region.
                </span>
              </h4>
              {/* <h5>Admin Login</h5> */}
              <form action="" className="row pt-2" onSubmit={handleLogin}>
                <div className="col-7">
                  <div className="form-group">
                    <label htmlFor="" className="mb-1">
                      Login
                    </label>
                    <input
                      type="text"
                      className="form-control mt-1"
                      placeholder="Email or phone number"
                      value={email}
                      onChange={(e) => setEmail(e.target.value)}
                      required
                    />
                  </div>
                  <div className="form-group">
                    <label htmlFor="" className="mb-1">
                      Password
                    </label>
                    <input
                      type="password"
                      className="form-control mt-1"
                      placeholder="Enter password"
                      value={password}
                      onChange={(e) => setPassword(e.target.value)}
                    />
                  </div>
                </div>

                <div className="d-flex justify-content-between align-items-center">
                  <div>
                    <a href="#" className="float-end text-primary1" onClick={handleForgetPassword}>
                      Forgot Password?
                    </a>
                  </div>
                </div>
                <div className="col-7 text-center">
                  <button type="submit" disabled={loading} className="btn btn-login px-4 float-end mt-4 w-100">
                    {loading ? "Logging in..." : "Login"}
                  </button>
                </div>
              </form>
              <br />
              <h4>
                <span className="text-dark font-14px" style={{ fontWeight: 'normal' }}>
                  Don't have an account? <Link to="/register-one">Register here</Link>
                </span>
              </h4>
              <div className="loginFooterLogo mt-3 pt-3">
                <div className="d-flex">
                  <img src="./assets/images/login-footer-1.svg" alt="" height={"40px"} />
                  <img className="ms-2" src="./assets/images/sadc.png" alt="" height={"40px"} />
                </div>
                <div className="mt-4 d-flex">
                  {/* <p> <img src="./assets/images/sadc.png" height={'40px'} /> <img src="./assets/images/aiccra_logo_1.png" height={'50px'} /> <img src="./assets/images/footerlogo-new.png" height={'40px'} /></p> */}
                  <img src="./assets/images/top-login-logo.svg" height={"44px"} alt="" />
                  <img className="mx-2" src="./assets/images/footer-2.png" height={"40px"} />
                  <img src="./assets/images/AICCRA.png" height={"40px"} alt="" />
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}

export default Login;